/* =============================================================================
 * LA PANTALLA EN LA DIRECCIÓN Y EN EL HISTORIAL (fix/recarga-conserva-pantalla)
 * -----------------------------------------------------------------------------
 * EL DEFECTO: en el teléfono, recargar deslizando hacia abajo devolvía a la
 * pantalla principal. La pantalla vivía SOLO en el estado de React
 * (`pantalla` del trabajador, `tab` y `equipoSel` del patrón en su portal móvil),
 * y una recarga completa lo reinicia. "Actualizar" no recarga la página —vuelve a
 * leer los datos—, por eso ahí sí se quedaba.
 *
 * AHORA la pantalla va en la dirección (`?vista=…`) y cada cambio de pantalla deja
 * una entrada en el historial del navegador:
 *   · una recarga vuelve a la misma pantalla;
 *   · "atrás" (el gesto o el botón de Android) vuelve a la pantalla anterior; en la
 *     principal, "atrás" hace lo de siempre (no hay nada debajo que sea nuestro);
 *   · el botón "‹" de la plataforma sigue igual: lleva a la principal, y aquí eso
 *     se traduce en VOLVER en el historial (no en apilar otra entrada), para que
 *     "atrás" en la principal siga saliendo como hoy.
 * Sin gestos propios: solo el historial del navegador.
 *
 * QUÉ PANTALLAS SON VÁLIDAS lo decide quien usa esto (`aplicar`): una dirección que
 * apunta a algo que ya no existe o que no es de ese rol devuelve "invalida", y se
 * cae a la principal sin error.
 *
 * LO ESCRITO A MEDIAS: si "atrás" va a sacar de una pantalla con algo sin guardar
 * (la misma pregunta que ya usa la plataforma: `hayCapturaSinGuardar`), se pide
 * confirmación; si dice que no, se queda donde estaba.
 *
 * NADA DE REACT AQUÍ: recibe la ventana (`win`), para poder probarlo con una de
 * mentira. CommonJS, exportado nombre por nombre (la convención de checador.js).
 * ========================================================================== */

const PARAM_VISTA = "vista";

// La vista que trae una dirección, o "" si no trae.
function vistaDeLaDireccion(href) {
  try { const v = new URL(href).searchParams.get(PARAM_VISTA); return v ? String(v) : ""; }
  catch (e) { return ""; }
}
// La dirección (ruta, consulta y ancla) con esa vista, o sin ella si es "".
// Lo demás de la dirección se conserva tal cual.
function direccionConVista(href, vista) {
  const u = new URL(href);
  if (vista) u.searchParams.set(PARAM_VISTA, vista); else u.searchParams.delete(PARAM_VISTA);
  return u.pathname + u.search + u.hash;
}

// o: { win, principal, aplicar(vista) → "ok" | "invalida", hayCaptura() → bool,
//      confirmar(texto) → bool, textoConfirmar }
// Devuelve { iniciar(mostrada), alCambiar(mostrada), alVolver(), estado() }.
//   · iniciar: al montar la pantalla (con sus datos listos): lee la dirección.
//   · alCambiar: cada vez que la pantalla que se ve cambia (la llama un efecto).
//   · alVolver: el evento `popstate` (atrás / adelante del navegador).
function crearNavegacion(o) {
  const win = o.win, principal = o.principal;
  let iniciado = false;
  let mostrada = principal;   // lo que la pantalla enseña (lo último que reportó)
  let esperando = null;       // una vista aplicada aquí que la pantalla aún no reporta
  let pila = [principal];     // la pila de la entrada actual del historial

  const pilaDelEstado = () => {
    const s = win.history.state;
    return s && Array.isArray(s.axisPila) && s.axisPila.length ? s.axisPila.map(String) : null;
  };
  const escribir = (modo, nueva) => {
    pila = nueva.slice();
    const v = pila[pila.length - 1];
    const estado = Object.assign({}, win.history.state || {}, { axisPila: pila.slice() });
    win.history[modo === "push" ? "pushState" : "replaceState"](estado, "", direccionConVista(win.location.href, v === principal ? "" : v));
  };
  // Aplica una vista; si no vale, la principal. Devuelve la que quedó.
  const poner = (v) => {
    let quedo = principal;
    if (v !== principal) { try { if (o.aplicar(v) === "ok") quedo = v; } catch (e) { quedo = principal; } }
    if (quedo === principal) { try { o.aplicar(principal); } catch (e) { /* la principal siempre se puede */ } }
    if (quedo !== mostrada) esperando = quedo;
    return quedo;
  };

  // `mostradaInicial` es lo que la pantalla enseña al iniciar: al montar, la
  // principal; el patrón inicia cuando ya tiene sus fichas, y para entonces pudo
  // haber tocado una pestaña.
  function iniciar(mostradaInicial) {
    if (iniciado) return;
    iniciado = true;
    mostrada = mostradaInicial || principal;
    const v = vistaDeLaDireccion(win.location.href);
    const deAntes = pilaDelEstado();
    if (v && v !== principal) {
      let ok = false;
      try { ok = o.aplicar(v) === "ok"; } catch (e) { ok = false; }
      if (ok) {
        if (v !== mostrada) esperando = v;
        // UNA RECARGA de una entrada nuestra trae su pila: se conserva tal cual.
        if (deAntes && deAntes[deAntes.length - 1] === v) { pila = deAntes; return; }
        // Se abrió directo en esa vista: la principal debajo, para que "atrás" la lleve ahí.
        escribir("replace", [principal]);
        escribir("push", [principal, v]);
        return;
      }
      // No existe o no es de este rol: la dirección se limpia y se queda lo que ya
      // se ve (al montar, la principal).
    }
    // SIN VISTA QUE RESTAURAR NO SE MUEVE LA PANTALLA: está en la principal, o
    // donde la llevó un aviso (?abrir=), que no se pisa.
    if (!v && deAntes && deAntes[deAntes.length - 1] === principal && mostrada === principal) { pila = deAntes; return; }
    escribir("replace", [principal]);
    if (mostrada !== principal) escribir("push", [principal, mostrada]);
  }

  function alCambiar(x) {
    if (!iniciado) return;
    if (esperando !== null) {
      if (x === esperando) { esperando = null; mostrada = x; return; }
      if (x === mostrada) return;   // el aviso de antes de aplicar: viejo
      esperando = null;             // otro la movió (un aviso, ?abrir=): se sigue de ahí
    }
    if (x === mostrada) return;
    mostrada = x;
    const actual = pilaDelEstado() || pila;
    if (x === principal) {
      // A LA PRINCIPAL SE VUELVE, no se apila: así "atrás" ahí hace lo de siempre.
      if (actual.length > 1) { pila = actual.slice(0, 1); win.history.go(-(actual.length - 1)); }
      else escribir("replace", [principal]);
      return;
    }
    // A la pantalla de justo antes, también se vuelve.
    if (actual.length >= 2 && actual[actual.length - 2] === x) { pila = actual.slice(0, -1); win.history.back(); return; }
    escribir("push", actual.concat([x]));
  }

  function alVolver() {
    if (!iniciado) return;
    const destinoPila = pilaDelEstado();
    const v = destinoPila ? destinoPila[destinoPila.length - 1] : (vistaDeLaDireccion(win.location.href) || principal);
    // YA ESTÁ AHÍ: es el "atrás" que provocó la propia plataforma (el "‹", o volver a
    // la pantalla anterior), con la pantalla ya cambiada. No se pregunta nada.
    if (v === mostrada) { pila = destinoPila || [principal]; return; }
    // Algo escrito sin guardar: se pregunta. Si no, se queda donde estaba.
    if (o.hayCaptura && o.hayCaptura() && !(o.confirmar && o.confirmar(o.textoConfirmar))) {
      const quedarse = pila.slice();
      escribir("push", quedarse);
      return;
    }
    const quedo = poner(v);
    if (quedo === v) pila = destinoPila || [principal];
    else escribir("replace", [principal]);
  }

  return { iniciar, alCambiar, alVolver, estado: () => ({ mostrada, esperando, pila: pila.slice() }) };
}

exports.PARAM_VISTA = PARAM_VISTA;
exports.vistaDeLaDireccion = vistaDeLaDireccion;
exports.direccionConVista = direccionConVista;
exports.crearNavegacion = crearNavegacion;
